import React from 'react';
import { BookOpenCheck, Check, FileCheck2, Loader2, MessageSquare, PenLine } from 'lucide-react';
import { useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useLoopingClock } from '@/hooks/useLoopingClock';

export type HomeProofStage = 'write' | 'ground' | 'review' | 'revise';

interface ProofStage {
  id: HomeProofStage;
  label: string;
  ask: string;
  working: string;
  result: string;
  icon: React.ElementType;
}

const STAGES: ProofStage[] = [
  { id: 'write', label: 'Write', ask: 'Draft the related work', working: 'Writing in LaTeX', result: 'Related work drafted', icon: PenLine },
  { id: 'ground', label: 'Ground', ask: 'Ground this claim', working: 'Resolving the literature', result: 'Citation and reference resolved', icon: BookOpenCheck },
  { id: 'review', label: 'Review', ask: 'Review the method', working: 'Checking the methodology', result: 'Sampling limitation identified', icon: MessageSquare },
  { id: 'revise', label: 'Revise', ask: 'Fix it', working: 'Revising and compiling', result: 'Revision compiled', icon: FileCheck2 },
];

const SCENE_SECONDS = 3.25;
const REST_SECONDS = 2;
const CYCLE_SECONDS = STAGES.length * SCENE_SECONDS + REST_SECONDS;
const RESULT_AT = 1.35;

interface HomeProductProofProps {
  intent?: HomeProofStage | null;
  paused?: boolean;
}

const HomeProductProof: React.FC<HomeProductProofProps> = ({ intent, paused = false }) => {
  const reducedMotion = useReducedMotion();
  const still = Boolean(reducedMotion) || paused;
  const { clock } = useLoopingClock(CYCLE_SECONDS, still || Boolean(intent));
  const intentIndex = intent ? STAGES.findIndex((stage) => stage.id === intent) : -1;
  const visibleIndex = still
    ? STAGES.length - 1
    : intentIndex >= 0
      ? intentIndex
      : Math.min(STAGES.length - 1, Math.floor(clock / SCENE_SECONDS));
  const active = STAGES[visibleIndex];
  const sceneClock = still || intentIndex >= 0 ? SCENE_SECONDS : clock - visibleIndex * SCENE_SECONDS;
  const resultVisible = still || sceneClock >= RESULT_AT;
  const written = still || visibleIndex > 0 || (visibleIndex === 0 && resultVisible);
  const grounded = still || visibleIndex > 1 || (visibleIndex === 1 && resultVisible);
  const reviewed = still || visibleIndex > 2 || (visibleIndex === 2 && resultVisible);
  const revised = still || (visibleIndex === 3 && resultVisible);

  return (
    <section className="border-y border-border/60 bg-background/80 px-5 py-14 md:px-8 md:py-20" aria-labelledby="product-proof-title">
      <div className="mx-auto max-w-6xl">
        <div className="mx-auto mb-8 max-w-2xl text-center md:mb-12">
          <p className="mb-3 text-xs font-semibold uppercase text-primary">One connected workflow</p>
          <h2 id="product-proof-title" className="text-3xl font-medium md:text-4xl">From first prompt to a stronger draft</h2>
          <p className="mt-4 text-base leading-7 text-muted-foreground md:text-lg">
            Work in the manuscript, ground claims in the literature, then challenge and improve the same paper.
          </p>
        </div>

        <div className="mx-auto max-w-4xl">
          <div className="mb-4 grid grid-cols-4 border-b border-border/70" role="list" aria-label="Writing workflow">
            {STAGES.map((stage, index) => {
              const Icon = stage.icon;
              const selected = index === visibleIndex;
              return (
                <div
                  key={stage.id}
                  role="listitem"
                  className={cn(
                    'flex min-w-0 flex-col items-center justify-center gap-1 border-b-2 px-0.5 pb-2 text-[11px] font-medium transition-colors sm:flex-row sm:gap-2 sm:pb-3 sm:text-sm',
                    selected ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground',
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                   <span>{stage.label}</span>
                </div>
              );
            })}
          </div>

          <div className="writer-surface overflow-hidden p-3 sm:p-6" aria-live="polite" data-testid="home-product-proof">
            <div className="mb-3 flex h-8 items-center justify-between border-b border-border/60 pb-3 text-xs text-muted-foreground sm:mb-4">
              <span className="font-mono">main.pdf</span>
              <span className="inline-flex max-w-[72%] items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 font-medium text-primary" data-testid="home-proof-request">
                “{active.ask}”
              </span>
            </div>

            <div className="mx-auto h-[10.5rem] max-w-2xl overflow-hidden border border-border/50 bg-background px-5 py-4 shadow-sm sm:hidden" data-testid="home-proof-mobile-canvas">
              <div className="mb-3 flex items-center gap-1.5 text-[10px] font-semibold uppercase text-muted-foreground">
                {resultVisible ? <Check className="h-3 w-3 text-primary" aria-hidden="true" /> : <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />}
                {resultVisible ? active.result : active.working}
              </div>
              {active.id === 'write' && (
                <div className={cn('transition-opacity', resultVisible ? 'opacity-100' : 'opacity-35')}>
                  <p className="font-serif text-xs font-semibold">Related work</p>
                  <p className="mt-2 font-serif text-xs leading-5 text-foreground/70">Recent work frames coordination as a learned behaviour rather than a fixed protocol.</p>
                </div>
              )}
              {active.id === 'ground' && (
                <div>
                  <p className="font-serif text-xs leading-5 text-foreground/70">Coordination can emerge from learned policies <span className={cn('rounded px-1 py-0.5 font-mono text-[10px]', resultVisible ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground')}>{resultVisible ? '[12]' : '[?]'}</span>.</p>
                  <p className={cn('mt-3 border-t border-border/50 pt-2 font-serif text-[10px] text-muted-foreground transition-opacity', resultVisible ? 'opacity-100' : 'opacity-25')}>[12] Learned coordination in distributed systems, 2026.</p>
                </div>
              )}
              {active.id === 'review' && (
                <div className={cn('border-l-2 pl-3 transition-opacity', resultVisible ? 'border-primary/50 opacity-100' : 'border-border opacity-30')}>
                  <p className="text-[10px] font-semibold uppercase text-muted-foreground">Methodology review</p>
                  <p className="mt-2 text-xs leading-5 text-foreground/80">State the sampling limits and compare against a fixed-protocol baseline.</p>
                </div>
              )}
              {active.id === 'revise' && (
                <div className={cn('space-y-3 text-xs leading-5 transition-opacity', resultVisible ? 'opacity-100' : 'opacity-30')}>
                  <p className="text-muted-foreground line-through decoration-muted-foreground/60">We tested the method on several datasets.</p>
                  <p className="border-l-2 border-primary/50 pl-3 text-foreground/80">We compared three baselines across four public datasets.</p>
                </div>
              )}
            </div>

            <div className="mx-auto hidden h-[21rem] max-w-2xl overflow-hidden border border-border/50 bg-background px-10 py-7 shadow-sm sm:block" data-testid="home-proof-desktop-canvas">
              <h3 className="text-center font-serif text-sm font-semibold sm:text-lg">Robust coordination in distributed systems</h3>
              <p className="mt-1 text-center font-serif text-[10px] italic text-muted-foreground sm:mt-2 sm:text-xs">Working manuscript</p>
              <h4 className={cn('mt-5 font-serif text-xs font-semibold transition-opacity sm:mt-7 sm:text-sm', written ? 'opacity-100' : 'opacity-25')}>Related work</h4>
              <p className={cn('mt-2 min-h-12 font-serif text-xs leading-5 text-foreground/75 transition-opacity sm:mt-3 sm:text-sm sm:leading-7', written ? 'opacity-100' : 'opacity-25')}>
                Recent work frames coordination as a learned behaviour rather than a fixed protocol
                <span className={cn('ml-1 rounded px-1.5 py-0.5 font-mono text-[10px] transition-colors sm:text-xs', grounded ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground')}>
                  {grounded ? '[12]' : '[?]'}
                </span>.
              </p>

              <div className={cn('mt-4 min-h-[4.5rem] border-l-2 pl-3 transition-opacity sm:mt-6 sm:pl-4', reviewed ? 'border-primary/50 opacity-100' : 'border-border opacity-20')}>
                <p className="text-[10px] font-semibold uppercase text-muted-foreground sm:text-xs">Methodology review</p>
                <p className="mt-1 text-xs leading-5 text-foreground/80 sm:mt-2 sm:text-sm sm:leading-6">State the sampling limits and compare against a fixed-protocol baseline.</p>
              </div>

              <div className={cn('mt-3 min-h-[3.5rem] text-xs transition-opacity sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-2 sm:text-sm', revised ? 'opacity-100' : 'opacity-20')}>
                <p className="hidden text-muted-foreground line-through decoration-muted-foreground/60 sm:block">We tested the method on several datasets.</p>
                <p className="border-l-2 border-primary/50 pl-3 text-foreground/80">We compared three baselines across four public datasets.</p>
              </div>
            </div>

            <div className="hidden h-9 items-end justify-center text-xs text-muted-foreground sm:flex" data-testid="home-proof-result">
              <span key={`${active.id}-${resultVisible}`} className="inline-flex animate-fade-in items-center gap-1.5">
                {resultVisible ? <Check className="h-3.5 w-3.5 text-primary" aria-hidden="true" /> : <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
                {resultVisible ? active.result : active.working}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeProductProof;